import React from 'react';
import { Link } from 'react-router-dom';
import { HiCalendar, HiArrowRight } from 'react-icons/hi';

const ArticleCard = ({ blog }) => {
  // Helper function to convert YouTube watch links to embed links (unchanged)
  const getEmbedUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('youtube.com') && urlObj.searchParams.has('v')) {
        const videoId = urlObj.searchParams.get('v');
        return `https://www.youtube.com/embed/${videoId}`;
      }
      if (urlObj.hostname.includes('youtu.be')) {
        const videoId = urlObj.pathname.slice(1);
        return `https://www.youtube.com/embed/${videoId}`;
      }
    } catch (error) {
      console.error("Invalid URL for video embed:", error);
      return '';
    }
    return url;
  };

  const videoEmbedUrl = getEmbedUrl(blog.video_link);

  // Define the core style that uses the dark purple background
  const cardStyle = "group relative bg-[#551756] p-6 rounded-lg shadow-lg text-white overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1";

  // Use the gold/beige color for a simulated "wavy" bottom section
  const wavyBackgroundStyle = {
    // This uses a CSS variable to apply the curve via clip-path, which
    // is often done with Tailwind configuration or custom CSS classes.
    // Since we're in JSX, we'll use an approach that adds a colored element
    // which is visually contained by the main card's overflow:hidden.
  };

  return (
    <div 
      key={blog._id} 
      className={cardStyle}
    >
      {/* 🌟 Wavy/Curved Bottom Accent using a pseudo-element visual effect 🌟 */}
      {/* This invisible container creates the colored, curved background effect */}
      <div 
        className="absolute bottom-0 left-0 w-full h-1/2"
        style={{
          // Use the gold/beige color for the wavy part
          backgroundColor: '#cab273', 
          // Use CSS clip-path to create the wavy/curved effect.
          // This specific polygon creates a smooth, subtle upward curve.
          clipPath: 'polygon(0% 40%, 100% 0%, 100% 100%, 0% 100%)',
          zIndex: 0, // Ensure it's behind the content
        }}
      ></div>

      {/* Conditionally render PDF badge */}
      {blog.pdf_drive_link && (
        <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg shadow-lg z-10">
          PDF
        </div>
      )}
      
      {/* Conditionally render Video Iframe or Image */}
      {videoEmbedUrl ? (
        <div className="aspect-video w-full overflow-hidden rounded-md mb-4 relative z-10">
          <iframe
            src={videoEmbedUrl}
            title={blog.title}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      ) : blog.image ? (
        <div className="overflow-hidden rounded-md mb-4 relative z-10">
          <img
            src={blog.image}
            alt={blog.title}
            className="w-full h-56 object-cover rounded-md transition-transform duration-500 group-hover:scale-110"
          />
        </div>
      ) : null}
      
      {/* Text needs high contrast, so we use a shadow on the text to ensure readability 
          where the text overlaps the gold/beige curve. */}
      <h2 
        className="text-2xl font-bold mb-2 line-clamp-2 text-white relative z-10"
        style={{ textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)' }}
      >
        {blog.title}
      </h2>
      
      <p className="flex items-center gap-2 text-sm text-gray-200 mb-4 relative z-10">
        <HiCalendar className="text-gray-200" />
        <span>{new Date(blog.created_at).toLocaleDateString()}</span>
      </p>
      
      <Link 
        to={`/blog/${blog.id}`} 
        className="inline-flex items-center gap-2 text-[#cab273] font-semibold group-hover:text-white transition-colors relative z-10"
      >
        <span>Read More</span>
        <HiArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </div>
  );
};

export default ArticleCard;